import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '@/context/PlatformAuthContext';
import PlatformLayout from '@/components/layout/PlatformLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Building2, TrendingUp, Users, Receipt, Tag, UserCheck, ArrowUpRight, ArrowDownRight, DollarSign } from 'lucide-react';

const PlatformDashboard = () => {
  const { fetchWithAuth } = usePlatformAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await fetchWithAuth('/api/platform/dashboard/stats');
        if (!response.ok) throw new Error('Failed to load stats');
        const data = await response.json();
        setStats(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [fetchWithAuth]);

  if (loading) {
    return (
      <PlatformLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </PlatformLayout>
    );
  }

  if (error) {
    return (
      <PlatformLayout>
        <div className="text-center py-12">
          <p className="text-red-400">{error}</p>
        </div>
      </PlatformLayout>
    );
  }

  const tierColors = {
    DEMO: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    STARTER: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    GROWTH: 'bg-green-500/10 text-green-400 border-green-500/20',
    TRADER: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    BUSINESS: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    ENTERPRISE: 'bg-pink-500/10 text-pink-400 border-pink-500/20'
  };

  return (
    <PlatformLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Platform Dashboard</h1>
          <p className="text-slate-400 mt-1">Overview of your FinOrchest platform</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Customers</p>
                  <p className="text-3xl font-bold text-white mt-1" data-testid="total-customers">
                    {stats?.total_organizations || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Monthly Revenue</p>
                  <p className="text-3xl font-bold text-white mt-1" data-testid="mrr">
                    {stats?.mrr_formatted || '₹0'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Transactions (This Month)</p>
                  <p className="text-3xl font-bold text-white mt-1" data-testid="transactions">
                    {stats?.transactions_this_month || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Active Sales Reps</p>
                  <p className="text-3xl font-bold text-white mt-1" data-testid="sales-reps">
                    {stats?.active_sales_reps || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Breakdown & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Subscription Breakdown */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Subscription Breakdown</CardTitle>
              <CardDescription className="text-slate-400">
                Active subscriptions by tier
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats?.subscription_tiers || {}).length > 0 ? (
                  Object.entries(stats.subscription_tiers).map(([tier, count]) => (
                    <div 
                      key={tier}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50"
                    >
                      <div className="flex items-center gap-3">
                        <Badge className={tierColors[tier] || 'bg-slate-600 text-slate-300'}>
                          {tier}
                        </Badge>
                      </div>
                      <span className="text-lg font-semibold text-white">{count}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-400 text-center py-4">No active subscriptions</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Quick Actions</CardTitle>
              <CardDescription className="text-slate-400">
                Common platform management tasks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <a 
                  href="/platform/customers"
                  className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors group"
                  data-testid="quick-action-customers"
                >
                  <Building2 className="w-6 h-6 text-blue-400 mb-2" />
                  <p className="font-medium text-white">View Customers</p>
                  <p className="text-xs text-slate-400 mt-1">Manage all organizations</p>
                </a>
                
                <a 
                  href="/platform/sales-reps"
                  className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors group"
                  data-testid="quick-action-sales"
                >
                  <UserCheck className="w-6 h-6 text-green-400 mb-2" />
                  <p className="font-medium text-white">Sales Team</p>
                  <p className="text-xs text-slate-400 mt-1">Manage sales reps</p>
                </a>
                
                <a 
                  href="/platform/coupons"
                  className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors group"
                  data-testid="quick-action-coupons"
                >
                  <Tag className="w-6 h-6 text-purple-400 mb-2" />
                  <p className="font-medium text-white">Coupons</p>
                  <p className="text-xs text-slate-400 mt-1">Create discount codes</p>
                </a>
                
                <a 
                  href="/platform/enterprise"
                  className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors group"
                  data-testid="quick-action-enterprise"
                >
                  <DollarSign className="w-6 h-6 text-orange-400 mb-2" />
                  <p className="font-medium text-white">Enterprise</p>
                  <p className="text-xs text-slate-400 mt-1">Activate custom plans</p>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coupons Used This Month */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                  <Tag className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Coupons Used This Month</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {stats?.coupons_used_this_month || 0}
                  </p>
                </div>
              </div>
              <a 
                href="/platform/coupons"
                className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
              >
                View all coupons
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </PlatformLayout>
  );
};

export default PlatformDashboard;
